import EventEmitter from 'eventemitter3';
import { init } from '@/standardChartManager/standardChart';
import styles from './styles.module.scss';
import type { Token } from '../../core/token';
import { deepMerge } from '../../util/data';
import { Option as StandardChartOption, StandardChartIns } from '../../helper/standardChart';

interface DataZoomEvent {
  start: number;
  end: number;
}

// ECharts 实例类型定义
interface EChartsInstance {
  getModel: () => {
    getComponent: (name: string) => {
      getValueRange: (axis: string, index: number) => number[];
    };
  };
}

// DataZoom Token 类型定义
interface DataZoomToken {
  type?: 'slider' | 'inside';
  show?: boolean;
  bottom?: number;
  height?: number;
  startValue?: number;
  endValue?: number;
  [key: string]: unknown;
}

type Config = {
  data: string[];
  token?: Partial<Token>;
  style?: Partial<StandardChartOption['dataZoom'][0]>;
};

type EventTypes = {
  valueRange: (valueRange: number[]) => void;
  dataZoom: (startIndex: number, endIndex: number) => void;
};

export const defaultToken: DataZoomToken = {
  type: 'slider',
  show: true,
  bottom: 0,
  height: 16
};

export default class CustomDataZoom extends EventEmitter<EventTypes> {
  private __dom!: HTMLElement;

  private __config: Config;

  private _option!: StandardChartOption;

  chartIns!: StandardChartIns;

  constructor(dom: HTMLElement, config: Config) {
    super();

    this.__dom = dom;
    this.__dom.classList.add(styles.wrap);
    this.__config = config;

    this.__initialize();
  }

  private __initialize() {
    this.__buildDom();
    this.__buildDataZoom();
    this.__event();
  }

  private __buildDom() {
    const $chart = document.createElement('div');
    $chart.classList.add(styles.chart);
    this.__dom.appendChild($chart);
  }

  private __setDomHeight(height: number) {
    this.__dom.style.height = `${height}px`;
  }

  private __buildDataZoom() {
    const xAxisData = this.__config.data;
    const { token, style = {} } = this.__config;
    const dataZoomData = Array(xAxisData.length).fill(0) as number[];
    const chartIns = init(this.__dom.firstChild as HTMLElement);
    // 优先级：用户配置 >> 默认配置 >> 主题配置
    const dataZoomToken = deepMerge(
      deepMerge(token?.dvHXKLine?.dataZoom ?? {}, defaultToken, {
        override: true
      }),
      style,
      {
        override: true
      }
    ) as DataZoomToken;
    this.__setDomHeight(dataZoomToken.height ?? 16);

    const option: StandardChartOption = {
      dataZoom: [dataZoomToken],
      tooltip: {},
      axisPointer: {},
      xAxis: [
        {
          __dataIndex: 0,
          __encodeField: 'x',
          type: 'category',
          data: xAxisData,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          splitLine: { show: false }
        }
      ],
      yAxis: [
        {
          __dataIndex: 0,
          __encodeField: ['y'],
          type: 'value',
          data: [],
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          splitLine: { show: false }
        }
      ],
      series: [
        {
          type: 'bar',
          __encodeField: 'y',
          name: 'data',
          data: dataZoomData
        }
      ]
    };
    this._option = option;
    chartIns.play({ option });

    this.chartIns = chartIns;
  }

  private __event() {
    const self = this;
    let [dataZoomStartIndex, dataZoomEndIndex] = [0, self.__config.data.length - 1];

    // 直接在 chartIns 上绑定事件,而不是在 echarts 实例上
    // echarts 实例在初始化时可能为 undefined
    self.chartIns.on('dv:afterupdate', (): void => {
      const echarts = self.chartIns.getECharts() as EChartsInstance | undefined;
      if (echarts) {
        const valueRange = echarts.getModel().getComponent('dataZoom').getValueRange('x', 0);
        self.emit('valueRange', valueRange);
      }
    });

    self.chartIns.on('datazoom', (event: DataZoomEvent): void => {
      // 使用 self.__config.data 而不是闭包捕获的 xAxisData
      // 确保使用最新的数据长度
      const currentDataLength = self.__config.data.length;
      const startIndex = Math.round((event.start / 100) * (currentDataLength - 1));
      const endIndex = Math.round((event.end / 100) * (currentDataLength - 1));

      if (startIndex !== dataZoomStartIndex || endIndex !== dataZoomEndIndex) {
        // 设置行情图的区间
        [dataZoomStartIndex, dataZoomEndIndex] = [startIndex, endIndex];

        self.emit('dataZoom', startIndex, endIndex);
      }
    });
  }

  update(data: string[]) {
    const option = this._option;
    this.__config.data = data;
    if (option.dataZoom?.[0]) {
      option.dataZoom[0].endValue = data.length - 1;
    }
    if (option.xAxis?.[0]) {
      option.xAxis[0].data = data;
    }
    if (Array.isArray(option.series) && option.series[0]) {
      option.series[0].data = Array(data.length).fill(0) as number[];
    }
    this.chartIns.play({ option });
    // 移除 this.__event() 调用,避免重复绑定事件
  }

  destroy() {
    this.chartIns.destroy();
    this.__dom.innerHTML = '';
  }
}
